<template>
  <div class="cite-button">
    <ArticleHeaderButton
      v-if="newArticleDetailsDisplay"
      ref="showCiteDialog"
      aria-label="Show cite dialog"
      button-text="Cite"
      emit-on-click="showCiteDialog"
      data-test-id="CiteButton"
      @showCiteDialog="onShowCiteDialog"
    >
      <CiteIcon />
    </ArticleHeaderButton>
    <v-btn
      v-else
      ref="showCiteDialog"
      aria-label="Show cite dialog"
      class="cite-button__nudge-left onShowCiteDialog ml-2"
      outlined
      rounded
      data-test-id="CiteButton"
      @click="onShowCiteDialog"
    >
      Cite
      <span class="d-sr-only">this article</span>
    </v-btn>

    <div id="abstractDialogContainer">
      <v-dialog
        ref="abstractDialogRef"
        v-model="showCiteDialog"
        attach="#abstractDialogContainer"
        max-width="500"
        @keydown.esc="onHideCiteDialog"
      >
        <v-card>
          <v-card-title>
            <h2
              id="abstract-dialog-title"
              class="headline"
              style="word-break: keep-all"
            >
              Citation
            </h2>
          </v-card-title>
          <v-card-text>
            <ComboboxBase
              ref="styleSearchInput"
              v-model="selectedStyle"
              :options="citationStyles"
              :searchable="true"
              no-options-label="No matching styles found"
              placeholder="Search for style"
              :name="ariaLabel"
              :aria-label="ariaLabel"
              @updateInput="onChooseStyle"
              @search="updateSearch"
            ></ComboboxBase>
            <div class="mt-4 pa-3 border-primary border-all border-thin">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div v-html="citeText" />
            </div>
            <div class="caption text-right overflow-visible">
              <v-btn
                v-clipboard:copy="citePlainText"
                v-clipboard:success="onCopySuccess"
                v-clipboard:error="onCopyError"
                class="onCopySuccess"
                :disabled="cantCopy"
                :aria-label="`${hasCopied ? 'copied' : 'copy'} to clipboard`"
                text
              >
                {{ `${hasCopied ? "Copied" : "Copy"} to clipboard` }}
              </v-btn>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="onHideCiteDialog"
              color="warning"
              text
              aria-label="close"
              @click="onHideCiteDialog"
              >Close</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import mapFlags from "~/plugins/utilities/mapFlags";

import debounce from "~/store/manage/utilities/debounce";
import runGraphQLQuery from "~/store/manage/utilities/runGraphQLQuery";
import citationStyleAutocomplete from "~/store/graphql/queries/citationStyleAutocomplete.graphql";
import getCitationString from "~/store/graphql/queries/getCitationString.graphql";
import ComboboxBase from "~/components/combobox/ComboboxBase.vue";
import ArticleHeaderButton from "~/components/articles/ArticleHeaderButton.vue";
import CiteIcon from "~/components/articles/components/icons/CiteIcon";

const PREFERRED_CITATION_STYLE = "PREFERRED_CITATION_STYLE";

const getCitationStylesWithDelay = debounce(async function getCitationStyles(
  val
) {
  this.isLoading = true;

  const params = { searchTerm: val };

  await runGraphQLQuery(citationStyleAutocomplete, params, this.$axios)
    .then((data) => {
      this.citationStyles = data.citationStyleAutocomplete;
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.error(error);
    })
    .finally(() => (this.isLoading = false));
},
500);

export default {
  name: "CiteButton",
  components: {
    ComboboxBase,
    ArticleHeaderButton,
    CiteIcon,
  },
  props: {
    article: { type: Object, required: true },
    citationStyle: { type: Object, default: undefined },
  },
  data() {
    return {
      showCiteDialog: false,
      citeText: "Loading Citation",
      citePlainText: undefined,
      hasCopied: false,
      cantCopy: true,
      citationStyles: [],
      search: null,
      selectedStyle: null,
    };
  },
  computed: {
    ...mapFlags(["newArticleDetailsDisplay"]),
    ariaLabel() {
      return "Citation - search for style";
    },
  },
  watch: {
    search(val) {
      if (!val || val.length < 3) {
        this.citationStyles = [];

        return;
      }

      getCitationStylesWithDelay.call(this, val);
    },
  },
  methods: {
    onShowCiteDialog() {
      let citationStyle = this.$localStorage.get(PREFERRED_CITATION_STYLE);

      if (citationStyle) {
        try {
          citationStyle = JSON.parse(citationStyle);
        } catch (e) {
          citationStyle = this.citationStyle;
        }
      } else {
        citationStyle = this.citationStyle;
      }

      this.showCiteDialog = true;
      this.hasCopied = false;
      this.cantCopy = true;

      this.selectedStyle = citationStyle;
      this.citationStyles = [citationStyle];

      this.loadCitation(citationStyle);
      this.setInitialFocus();
      this.setAriaLabelAttribute();
    },
    setInitialFocus() {
      this.$nextTick(() => {
        this.$refs.styleSearchInput.focusCombobox();
      });
    },
    setAriaLabelAttribute() {
      this.$nextTick(() => {
        const dialogRef = this?.$refs?.abstractDialogRef?.$children[0]?.$el;

        if (dialogRef) {
          const titleByElementId = document.getElementById(
            "abstract-dialog-title"
          ).textContent;
          dialogRef.setAttribute("aria-label", titleByElementId);
        }
      });
    },
    onCopySuccess() {
      this.hasCopied = true;
    },
    onCopyError(e) {
      throw e;
    },
    onHideCiteDialog() {
      this.showCiteDialog = false;
      this.citeText = "Loading Citation";
      this.returnFocus();
    },
    onChooseStyle(style) {
      if (style) {
        this.selectedStyle = style;

        this.loadCitation(style);

        this.$localStorage.set(PREFERRED_CITATION_STYLE, JSON.stringify(style));
      }
    },
    loadCitation(citationStyle) {
      this.citeText = "Loading Citation";
      this.hasCopied = false;

      const params = {
        itemId: this.article.id,
        citationStyleKey: citationStyle.key,
      };

      runGraphQLQuery(getCitationString, params, this.$axios).then((data) => {
        const text = data.getCitationString;

        this.citeText = this.$sanitize(
          text + " This content is a preprint and has not been peer-reviewed."
        );
        this.citePlainText = this.$sanitize(
          text + " This content is a preprint and has not been peer-reviewed.",
          { allowedTags: [], allowedAttributes: {}, selfClosing: [] }
        );
        this.cantCopy = false;

        const map = { amp: "&", lt: "<", gt: ">", quot: '"', "#039": "'" };

        this.citePlainText = this.citePlainText.replace(
          /&([^;]+);/g,
          (m, c) => map[c] || ""
        );
      });
    },
    returnFocus() {
      this.$nextTick(() => {
        this.$refs.showCiteDialog?.$el.focus();
      });
    },
    updateSearch(value) {
      this.search = value;
    },
  },
};
</script>

<style scoped lang="scss">
.cite-button {
  &__nudge-left {
    margin-top: 16px;
    margin-left: 0;
    width: 160px;
  }
}
</style>
